import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorial } from './TutorialContext';
import { useAuth } from '../../context/AuthContext';
import { hasConfiguredClientExperience } from '../../client-experience/utils/companyAccess';
import { hasAddedEmployees } from './utils/hasAddedEmployees';
import { parseSettingsSectionPath } from '../settings/section-paths/settingsSectionPaths';

const SETTINGS_SECTION_TO_STEP = {
  business: 1,
  hours: 2,
  'services-categories': 3,
  'client-experience': 7,
  payments: 8,
  authorize: 8,
  deluxe: 8,
};

const ROUTE_TO_STEP_MAP = {
  '/app/overview/company-management': 4,
  '/app/overview/calendar-management': 5,
};

const getTutorialStepId = (location) => {
  const settingsSection = parseSettingsSectionPath(location.pathname, location.search).section;
  if (location.pathname.startsWith('/app/overview/settings') && SETTINGS_SECTION_TO_STEP[settingsSection]) {
    return SETTINGS_SECTION_TO_STEP[settingsSection];
  }
  return ROUTE_TO_STEP_MAP[location.pathname];
};

// Map steps to their completion criteria
const STEP_COMPLETION_CRITERIA = {
  // Company Profile - align with BusinessInformation form shape
  1: (formData) => {
    if (!formData) return false;
    const hasName = Boolean(formData.name);
    const hasPhone = Boolean(formData.phone);
    const hasAddress = Boolean(
      (formData.address && (
        formData.address.street1 || formData.address.city || formData.address.zipCode
      ))
    );
    return hasName && hasPhone && hasAddress;
  },
  2: (formData) => formData.businessHours && Object.keys(formData.businessHours).length > 0,
  3: (formData) => formData.services && formData.services.length > 0,
  4: (_formData, companyData) => hasAddedEmployees(companyData),
  5: (formData) => formData.calendarConnected,
  7: (_formData, companyData) => hasConfiguredClientExperience(companyData),
  8: (_formData, companyData) => {
    return Boolean(companyData?.deluxe?.connection?.connected === true);
  },
};

export const useTutorialProgress = () => {
  const location = useLocation();
  const { markStepComplete, completedSteps } = useTutorial();
  const { companyData } = useAuth();

  // Check if the current route corresponds to a tutorial step
  useEffect(() => {
    const stepId = getTutorialStepId(location);

    if (stepId && !completedSteps.includes(stepId)) {
      console.log('User is on tutorial step:', stepId);
    }
  }, [location, completedSteps]);

  // Function to check if a step should be marked as complete
  const checkStepCompletion = (stepId, formData) => {
    const criteria = STEP_COMPLETION_CRITERIA[stepId];
    return criteria ? criteria(formData, companyData) : false;
  };

  // Function to be called by components when their setup is complete
  const completeCurrentStep = (formData = null) => {
    const stepId = getTutorialStepId(location);
    
    if (stepId && !completedSteps.includes(stepId)) {
      // If formData is provided, check against completion criteria
      if (formData) {
        const isComplete = checkStepCompletion(stepId, formData);
        if (isComplete) {
          markStepComplete(stepId);
          return true;
        }
        return false;
      } else {
        // If no formData provided, mark as complete immediately
        markStepComplete(stepId);
        return true;
      }
    }
    return false;
  };

  return {
    completeCurrentStep,
    checkStepCompletion,
    currentStepId: getTutorialStepId(location),
  };
};

export default useTutorialProgress; 